import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Col, Row, Select, Spin, Typography} from 'antd';
import {
    BellOutlined,
    BulbOutlined,
    CheckOutlined,
    HomeOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    MobileOutlined,
    SettingOutlined,
    ShopOutlined,
    TrophyOutlined,
    UsergroupAddOutlined
} from '@ant-design/icons';
import DeviceIdentifier from '../components/DeviceIdentifier';
import {loadAvailableViews, setViewForDevice} from '../services/Api';

const {Title, Text, Paragraph} = Typography;
const {Option} = Select;

const DeviceIdentification = ({fingerprint, viewName, viewId}) => {
    const [availableViews, setAvailableViews] = useState({});
    const [selectedView, setSelectedView] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [assignmentStatus, setAssignmentStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadViews();
    }, []);

    const loadViews = async () => {
        try {
            setLoading(true);
            const views = await loadAvailableViews();
            setAvailableViews(views);
        } catch (error) {
            console.error('Error loading available views:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewAssignment = async () => {
        if (!selectedView) {
            setAssignmentStatus({type: 'error', message: 'Proszę wybrać widok!'});
            return;
        }

        setIsAssigning(true);
        setAssignmentStatus(null);

        try {
            await setViewForDevice(fingerprint, selectedView);

            setAssignmentStatus({
                type: 'success',
                message: `Widok "${availableViews[selectedView]}" został przypisany! Strona zostanie odświeżona za 2 sekundy.`
            });

            setTimeout(() => {
                window.location.reload();
            }, 2000);

        } catch (error) {
            console.error('Error assigning view:', error);
            setAssignmentStatus({
                type: 'error',
                message: 'Błąd podczas przypisywania widoku: ' + error.message
            });
        } finally {
            setIsAssigning(false);
        }
    };

    const getDeviceTypeRecommendation = () => {
        const userAgent = navigator.userAgent;
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
            return 'mobile';
        }
        return 'default';
    };

    const recommendedView = getDeviceTypeRecommendation();

    const viewConfigs = {
        'default': {
            icon: <HomeOutlined/>,
            color: '#007bff',
            title: 'Widok Domyślny',
            description: 'Standardowy widok identyfikacji urządzenia. Pozwala na konfigurację i przypisanie odpowiedniego widoku.'
        },
        'leaderboard': {
            icon: <TrophyOutlined/>,
            color: '#ffc107',
            title: 'Tablica Wyników',
            description: 'Wyświetla ranking graczy, statystyki i postępy gry. Idealny do prezentacji wyników na dużych monitorach i telewizorach.'
        },
        'mobile': {
            icon: <MobileOutlined/>,
            color: '#6f42c1',
            title: 'Terminal Mobilny',
            description: 'Zoptymalizowany terminal dla urządzeń mobilnych. Umożliwia graczom wprowadzanie wyników poprzez smartfony i tablety.'
        },
        'kiosk': {
            icon: <ShopOutlined/>,
            color: '#17a2b8',
            title: 'Terminal Kiosku',
            description: 'Terminal dedykowany dla określonego dołka z obsługą NFC. Gracze skanują swoje tagi NFC i wprowadzają wyniki dla konkretnego dołka.'
        },
        'reception': {
            icon: <BellOutlined/>,
            color: '#28a745',
            title: 'Terminal Recepcji',
            description: 'Terminal do zarządzania graczami i rozpoczynania gier. Umożliwia rejestrację nowych graczy, przypisywanie awatarów i tagów NFC.'
        },
        'register-players': {
            icon: <UsergroupAddOutlined />,
            color: '#28a745',
            title: 'Terminal Recepcji',
            description: 'Terminal do zarządzania graczami i rozpoczynania gier. Umożliwia rejestrację nowych graczy, przypisywanie awatarów i tagów NFC.'
        }
    };

    return (
        <div className="container">
            <div className="overlay-gradient"></div>
            <div className="layout-grid" style={{position: 'relative', zIndex: 1}}>

                {/* Header */}
                <div className="grid-span-8" style={{textAlign: 'center', marginBottom: 32}}>
                    <Title level={1} className="tablet" style={{color: 'var(--color-white)', margin: 0}}>
                        <SettingOutlined style={{marginRight: 16, color: 'var(--color-light-green)'}}/>
                        Konfiguracja Urządzenia
                    </Title>
                    <Paragraph style={{color: 'var(--white-60)', fontSize: '18px', marginTop: 16}}>
                        To urządzenie nie ma przypisanego widoku. Wybierz odpowiedni widok poniżej.
                    </Paragraph>

                    {viewId && (
                        <Alert
                            message="Development Mode"
                            description={`ViewID = ${viewId}`}
                            type="warning"
                            showIcon
                            style={{
                                backgroundColor: 'var(--white-10)',
                                border: '1px solid #ffc107',
                                maxWidth: '400px',
                                margin: '16px auto 0'
                            }}
                        />
                    )}
                </div>

                {/* Lewa kolumna - Przypisywanie widoku */}
                <div className="grid-span-4" style={{marginBottom: 32}}>
                    <Card className="card" style={{height: '100%'}}>
                        <Title level={3} style={{color: 'var(--color-white)', marginBottom: 24}}>
                            <MobileOutlined style={{marginRight: 8, color: 'var(--color-light-green)'}}/>
                            Wybierz Widok
                        </Title>

                        {loading ? (
                            <div style={{textAlign: 'center', padding: '40px 0'}}>
                                <Spin
                                    indicator={<LoadingOutlined style={{fontSize: 32, color: 'var(--color-white)'}}
                                                                spin/>}
                                    size="large"
                                />
                                <Paragraph style={{color: 'var(--white-60)', marginTop: 16}}>
                                    Ładowanie dostępnych widoków...
                                </Paragraph>
                            </div>
                        ) : (
                            <div className="stack-v-lg">
                                <div className="field">
                                    <div className="field__label">
                                        Dostępne widoki
                                    </div>
                                    <Select
                                        size="large"
                                        value={selectedView}
                                        onChange={setSelectedView}
                                        placeholder="-- Wybierz widok --"
                                        style={{width: '100%'}}
                                        className="config-select"
                                    >
                                        {Object.entries(availableViews).map(([key, label]) => (
                                            <Option
                                                key={key}
                                                value={key}
                                                style={{
                                                    fontWeight: key === recommendedView ? 'bold' : 'normal'
                                                }}
                                            >
                                                {viewConfigs[key]?.icon} {label} {key === recommendedView ? '(zalecany)' : ''}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>

                                {/* Sugestia */}
                                {recommendedView && availableViews[recommendedView] && (
                                    <Alert
                                        message="Sugestia systemu"
                                        description={
                                            <div>
                                                <Text style={{color: 'var(--color-white)'}}>
                                                    Na podstawie twojego urządzenia polecamy widok
                                                    "{availableViews[recommendedView]}".
                                                </Text>
                                                <Button
                                                    size="small"
                                                    type="link"
                                                    onClick={() => setSelectedView(recommendedView)}
                                                    style={{
                                                        color: 'var(--color-light-green)',
                                                        padding: '0 8px',
                                                        height: 'auto'
                                                    }}
                                                >
                                                    Wybierz
                                                </Button>
                                            </div>
                                        }
                                        type="info"
                                        icon={<BulbOutlined/>}
                                        showIcon
                                        style={{
                                            backgroundColor: 'var(--white-10)',
                                            border: '1px solid var(--color-light-green)',
                                        }}
                                    />
                                )}

                                {/* Status przypisania */}
                                {assignmentStatus && (
                                    <Alert
                                        message={assignmentStatus.type === 'success' ? 'Sukces!' : 'Błąd!'}
                                        description={assignmentStatus.message}
                                        type={assignmentStatus.type}
                                        showIcon
                                        style={{
                                            backgroundColor: 'var(--white-10)',
                                            border: assignmentStatus.type === 'success' ?
                                                '1px solid #52c41a' : '1px solid #ff4d4f',
                                        }}
                                    />
                                )}

                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={handleViewAssignment}
                                    disabled={isAssigning || !selectedView}
                                    loading={isAssigning}
                                    icon={<CheckOutlined/>}
                                    className="button primary large"
                                    style={{width: '100%', height: '56px', fontSize: '16px'}}
                                >
                                    {isAssigning ? 'Przypisywanie...' : 'Przypisz Widok'}
                                </Button>

                                <Text style={{
                                    color: 'var(--white-60)',
                                    fontSize: '12px',
                                    textAlign: 'center',
                                    display: 'block'
                                }}>
                                    Po przypisaniu widoku strona zostanie automatycznie odświeżona
                                </Text>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Prawa kolumna - DeviceIdentifier */}
                <div className="grid-span-4" style={{marginBottom: 32}}>
                    <Card className="card" style={{height: '100%'}}>
                        <DeviceIdentifier/>
                    </Card>
                </div>

                {/* Opis widoków */}
                <div className="grid-span-8">
                    <Card className="card">
                        <Title level={3} style={{color: 'var(--color-white)', marginBottom: 24}}>
                            <InfoCircleOutlined style={{marginRight: 8}}/>
                            Opis Widoków
                        </Title>

                        <Row gutter={[16, 16]}>
                            {Object.entries(viewConfigs).map(([key, config]) => (
                                <Col xs={24} sm={12} lg={8} key={key}>
                                    <div style={{
                                        padding: '20px',
                                        background: 'var(--white-10)',
                                        border: '1px solid var(--white-30)',
                                        borderRadius: 'var(--radius-8)',
                                        height: '100%',
                                        transition: 'all 0.2s ease'
                                    }}
                                         className="view-description-card"
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{
                                                fontSize: '24px',
                                                color: config.color,
                                                marginRight: '8px'
                                            }}>
                                                {config.icon}
                                            </div>
                                            <Title level={5} style={{
                                                color: config.color,
                                                margin: 0,
                                                fontSize: '16px'
                                            }}>
                                                {config.title.replace(/^[🏠🏆📱🏪🎯]\s/, '')}
                                            </Title>
                                        </div>
                                        <Paragraph style={{
                                            color: 'var(--white-80)',
                                            fontSize: '14px',
                                            lineHeight: '1.5',
                                            margin: 0
                                        }}>
                                            {config.description}
                                        </Paragraph>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default DeviceIdentification;